/**
 * RecipeDetailPage — Full recipe view from Spoonacular
 * Route: /recipes?dish=pasta
 */
import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, Users, ChefHat, Sparkles, ExternalLink, ListPlus, Youtube } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RecipeDetailPage = () => {
  const [searchParams] = useSearchParams();
  const dish = searchParams.get('dish') || searchParams.get('q') || 'pasta';
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [ingLoading, setIngLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/recipes/search?query=${encodeURIComponent(dish)}&number=5`)
      .then(r => r.json())
      .then(d => {
        setRecipes(d.results || []);
        if (d.results?.[0]) setSelected(d.results[0]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dish]);

  useEffect(() => {
    if (!selected?.id) return;
    setIngLoading(true);
    fetch(`${API_URL}/api/recipes/ingredients?recipe_id=${selected.id}`)
      .then(r => r.json())
      .then(d => setIngredients(d.ingredients || []))
      .catch(() => {})
      .finally(() => setIngLoading(false));
  }, [selected?.id]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-20 md:pb-8">
      {/* Hero */}
      <div className="relative" style={{ minHeight: '200px' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${selected?.image || 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1400&q=80'})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/70" />
        <div className="relative flex flex-col justify-end max-w-4xl mx-auto px-4 sm:px-6" style={{ minHeight: '200px' }}>
          <div className="w-full md:w-fit bg-black/45 backdrop-blur-md rounded-2xl p-4 md:p-5 mb-5">
            <div className="flex items-start gap-3">
              <div className="w-[3px] rounded-full bg-[#FF6B35] self-stretch min-h-[40px] flex-shrink-0" />
              <div>
                <Link to="/" className="inline-flex items-center gap-1 text-white/60 hover:text-white text-xs mb-1.5 transition-colors">
                  <ArrowLeft className="w-3 h-3" /> Back
                </Link>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white leading-tight">
                  Recipes for <span className="text-[#F4C430]">{dish}</span>
                </h1>
                <p className="text-white/60 text-xs mt-1 flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-[#F4C430]" /> Powered by Spoonacular
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {loading ? (
          <div className="space-y-4">
            {[1,2,3].map(i => <div key={i} className="h-24 bg-gray-100 rounded-2xl animate-pulse" />)}
          </div>
        ) : recipes.length === 0 ? (
          <div className="text-center py-16">
            <ChefHat className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-700 mb-2">No recipes found</h3>
            <p className="text-sm text-gray-500">Try searching for a different dish</p>
          </div>
        ) : (
          <div className="flex gap-6">
            {/* Recipe list */}
            <div className="w-full md:w-1/3 space-y-2 flex-shrink-0">
              {recipes.map(r => (
                <button key={r.id} onClick={() => setSelected(r)}
                  className={`w-full text-left p-3 rounded-2xl transition-all ${selected?.id === r.id ? 'bg-white shadow-md ring-2 ring-[#FF6B35]/20' : 'bg-white/50 hover:bg-white'}`}>
                  <div className="flex items-center gap-3">
                    <img src={r.image} alt={r.title} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=100&q=60'; }} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 line-clamp-1">{r.title}</p>
                      <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-500">
                        {r.readyInMinutes && <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{r.readyInMinutes}min</span>}
                        {r.servings && <span className="flex items-center gap-0.5"><Users className="w-3 h-3" />{r.servings} servings</span>}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Selected recipe detail */}
            {selected && (
              <div className="hidden md:block flex-1">
                <div className="bg-white rounded-[20px] overflow-hidden shadow-sm">
                  <img src={selected.image} alt={selected.title} className="w-full h-48 object-cover" />
                  <div className="p-5">
                    <h2 className="text-xl font-bold text-gray-900 mb-2">{selected.title}</h2>
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                      {selected.readyInMinutes && <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-[#FF6B35]" />{selected.readyInMinutes} min</span>}
                      {selected.servings && <span className="flex items-center gap-1"><Users className="w-4 h-4 text-[#FF6B35]" />{selected.servings} servings</span>}
                    </div>

                    {/* Ingredients */}
                    <h3 className="font-bold text-gray-900 text-sm mb-2 flex items-center gap-1.5"><ListPlus className="w-4 h-4 text-emerald-500" /> Ingredients</h3>
                    {ingLoading ? (
                      <div className="space-y-1.5">{[1,2,3,4].map(i => <div key={i} className="h-4 bg-gray-100 rounded animate-pulse w-3/4" />)}</div>
                    ) : ingredients.length > 0 ? (
                      <ul className="space-y-1.5 mb-5">
                        {ingredients.map((ing, i) => (
                          <li key={i} className="text-sm text-gray-700 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-[#FF6B35] rounded-full flex-shrink-0" />
                            <span className="text-gray-500 text-xs w-16 flex-shrink-0">{ing.amount}</span>
                            {ing.name}
                          </li>
                        ))}
                      </ul>
                    ) : <p className="text-xs text-gray-400 mb-4">No ingredients data</p>}

                    {/* Actions */}
                    <div className="flex gap-2 mt-4">
                      {selected.sourceUrl && (
                        <a href={selected.sourceUrl} target="_blank" rel="noopener noreferrer"
                          className="flex-1 py-2.5 bg-gray-900 text-white font-semibold text-sm rounded-2xl flex items-center justify-center gap-1.5 hover:bg-gray-800 transition-colors">
                          <ExternalLink className="w-4 h-4" /> Full Recipe
                        </a>
                      )}
                      <a href={`https://www.youtube.com/results?search_query=${encodeURIComponent(selected.title + ' recipe')}`} target="_blank" rel="noopener noreferrer"
                        className="flex-1 py-2.5 bg-gray-100 text-gray-700 font-semibold text-sm rounded-2xl flex items-center justify-center gap-1.5 hover:bg-gray-200 transition-colors">
                        <Youtube className="w-4 h-4 text-red-500" /> Watch Video
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecipeDetailPage;
